import React, {
  Context,
  createContext,
  useContext,
  useState,
  useEffect,
} from 'react';
import { useAuthContext } from './Auth';
import { useHistory, useLocation } from 'react-router';
import { enforceOnboarding, setUserAttribute } from '../utils';

export interface IOnboardingContext {
  goBack: (...args) => any;
  goForward: (...args) => any;
  pageChanged: boolean;
  setPageChanged: (...args) => any;
  activeStep: number;
  setActiveStep: (...args) => any;
  stepInStepper: number;
  setStepInStepper: (...args) => any;
  loading: boolean;
  setLoading: (...args) => any;
  hasCompletedOnboarding: boolean;
  setHasCompletedOnboarding: (...args) => any;
  justCompletedOnboarding: boolean;
  setJustCompletedOnboarding: (...args) => void;
  finishedSettingPageNumbers: boolean;
  setFinishedSettingPageNumbers: (arg: boolean) => void;
}

export const OnboardingContext: Context<IOnboardingContext> = createContext({
  goBack: () => {},
  goForward: () => {},
  pageChanged: null,
  setPageChanged: () => {},
  activeStep: 0,
  setActiveStep: () => {},
  stepInStepper: null,
  setStepInStepper: () => {},
  loading: null,
  setLoading: () => {},
  hasCompletedOnboarding: null,
  setHasCompletedOnboarding: () => {},
  justCompletedOnboarding: false,
  setJustCompletedOnboarding: () => {},
  finishedSettingPageNumbers: false,
  setFinishedSettingPageNumbers: () => {},
});

export default function OnboardingContextProvider(props) {
  const [activeStep, setActiveStep] = useState(null);
  const [pageChanged, setPageChanged] = useState(false);
  const [stepInStepper, setStepInStepper] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(null);
  const [justCompletedOnboarding, setJustCompletedOnboarding] = useState(false);
  const [finishedSettingPageNumbers, setFinishedSettingPageNumbers] =
    useState<boolean>(false);

  const { signedIn } = useAuthContext();
  const history = useHistory();
  const location = useLocation();

  function goBack() {
    setActiveStep(activeStep - 1);
    setPageChanged(true);
  }

  function goForward() {
    setActiveStep(activeStep + 1);
    setPageChanged(true);
  }

  useEffect(() => {
    if (signedIn && !hasCompletedOnboarding) {
      enforceOnboarding(setHasCompletedOnboarding, setActiveStep, history);
    }
    if (!signedIn && hasCompletedOnboarding) {
      setHasCompletedOnboarding(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn, history, location.pathname]);

  useEffect(() => {
    if (activeStep) {
      setUserAttribute('custom:onboarding_page', activeStep?.toString()).catch(
        () => {},
      );
    }
  }, [activeStep]);

  return (
    <OnboardingContext.Provider
      value={{
        goBack,
        goForward,
        pageChanged,
        setPageChanged,
        activeStep,
        setActiveStep,
        stepInStepper,
        setStepInStepper,
        loading,
        setLoading,
        hasCompletedOnboarding,
        setHasCompletedOnboarding,
        justCompletedOnboarding,
        setJustCompletedOnboarding,
        finishedSettingPageNumbers,
        setFinishedSettingPageNumbers,
      }}
    >
      {props.children}
    </OnboardingContext.Provider>
  );
}

export const useOnboardingContext = () => useContext(OnboardingContext);
